import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { NavMode } from '../hooks/useAppNavigation';
import { getNextMenuItemIndex, MenuNavigationKey } from './contextMenuKeyboard';

const MenuStyle = styled.div<{ x: number; y: number }>`
  position: fixed;
  left: ${({ x }) => Math.max(8, Math.min(x, window.innerWidth - 300))}px;
  top: ${({ y }) => Math.max(8, Math.min(y, window.innerHeight - 200))}px;
  z-index: 10000;
  background: var(--theme-surface, #fff);
  color: var(--theme-text, #333);
  border: 1px solid var(--theme-border, #ddd);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  padding: 8px 0;
  width: min(280px, calc(100vw - 16px));
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 13px;
`;

const Header = styled.div`
  padding: 4px 16px 8px;
  font-weight: 600;
  border-bottom: 1px solid var(--theme-border, #eee);
  margin-bottom: 4px;
  color: var(--theme-primary, #262262);
  font-size: 12px;
`;

const MatrixTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th {
    font-size: 10px;
    color: var(--theme-text-muted, #888);
    font-weight: 500;
    padding: 4px 8px;
    text-align: center;
  }

  td {
    padding: 2px 4px;
    text-align: center;
  }

  td:first-child {
    text-align: left;
    padding: 6px 16px;
    font-weight: 500;
    cursor: pointer;

    &:hover {
      background: var(--theme-surface-alt, #f0f0f0);
    }
  }
`;

const ActionBtn = styled.button`
  width: 100%;
  height: 100%;
  padding: 4px 0;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--theme-text, #333);
  border-radius: 4px;
  font-size: 14px;

  &:hover {
    background: var(--theme-surface-alt, #e8e8e8);
  }
`;

const UtilityAction = styled.button`
  width: 100%;
  padding: 6px 16px;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  border-top: 1px solid var(--theme-border, #eee);
  margin-top: 4px;
  font: inherit;
  text-align: left;

  &:hover {
    background: var(--theme-surface-alt, #f5f5f5);
  }

  &:focus-visible {
    outline: 3px solid var(--theme-primary, #4f46e5);
    outline-offset: -3px;
  }
`;

const SectionHeader = styled.div`
  padding: 8px 16px 4px;
  font-size: 10px;
  font-weight: 600;
  color: var(--theme-text-muted, #888);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-top: 1px solid var(--theme-border, #eee);
  margin-top: 4px;
`;

const UtilityRow = styled.button`
  width: 100%;
  padding: 6px 16px;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font: inherit;
  font-size: 13px;
  text-align: left;

  &:hover {
    background: var(--theme-surface-alt, #f5f5f5);
  }

  &:focus-visible {
    outline: 3px solid var(--theme-primary, #4f46e5);
    outline-offset: -3px;
  }
`;

export interface ContextMenuTarget<TAction = unknown> {
  label: string;
  /** Typed semantic payload preferred by entity-aware menus. */
  action?: TAction;
  /** Legacy result-only payload retained for existing menus. */
  resultIndex?: any;
  // Optional: custom action instead of navigation (e.g., for copy actions)
  onClick?: () => void;
  // Optional: icon to show before the label
  icon?: string;
}

export interface ContextMenuSection {
  label?: string; // Optional section header
  targets: ContextMenuTarget[];
}

export interface UnifiedContextMenuProps {
  x: number;
  y: number;
  title: React.ReactNode;
  // Support both old (flat array) and new (sections) format
  targets?: ContextMenuTarget[];
  sections?: ContextMenuSection[];
  onNavigate: (mode: NavMode, targetIndex: any) => void;
  onCopy?: () => void;
  copyLabel?: string;
  onClose: () => void;
}

export const UnifiedContextMenu: React.FC<UnifiedContextMenuProps> = ({
  x, y, title, targets, sections, onNavigate, onCopy, copyLabel = "Copy ID", onClose
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const returnFocusTo = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const handleClick = () => onCloseRef.current();
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
      }
    };
    const timeoutId = window.setTimeout(() => {
      menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
      document.addEventListener('click', handleClick);
      document.addEventListener('contextmenu', handleClick);
      document.addEventListener('keydown', handleKey);
    }, 0);
    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('contextmenu', handleClick);
      document.removeEventListener('keydown', handleKey);
      if (returnFocusTo?.isConnected) {
        returnFocusTo.focus();
      }
    };
  }, []);

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    event.stopPropagation();
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') || []
    );
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
    const nextIndex = getNextMenuItemIndex(
      event.key as MenuNavigationKey,
      currentIndex,
      items.length
    );
    items[nextIndex]?.focus();
  };

  // Convert flat targets to sections format for uniform rendering
  const sectionsToRender: ContextMenuSection[] = sections || (targets ? [{ targets }] : []);

  return (
    <MenuStyle
      ref={menuRef}
      x={x}
      y={y}
      role="menu"
      aria-label="Context actions"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={handleMenuKeyDown}
    >
      <Header>{title}</Header>
      {sectionsToRender.map((section, sectionIdx) => {
        // Separate navigation targets from utility targets
        const navTargets = section.targets.filter(t => !t.onClick);
        const utilityTargets = section.targets.filter(t => t.onClick);

        return (
          <React.Fragment key={sectionIdx}>
            {section.label && <SectionHeader>{section.label}</SectionHeader>}

            {/* Navigation targets as flat menu */}
            {navTargets.map((t, idx) => (
              <React.Fragment key={`nav-${idx}`}>
                <UtilityRow
                  type="button"
                  role="menuitem"
                  aria-label={t.label}
                  onClick={() => onNavigate('split', t.action ?? t.resultIndex)}
                >
                  {t.icon && <span style={{ marginRight: 4 }}>{t.icon}</span>}
                  {t.label}
                </UtilityRow>
                <UtilityRow
                  type="button"
                  role="menuitem"
                  aria-label={`Open ${t.label} in new tab`}
                  onClick={() => onNavigate('newTab', t.action ?? t.resultIndex)}
                >
                  <span style={{ marginRight: 4, opacity: 0.6 }}>↗</span>
                  Open {t.label} in new tab
                </UtilityRow>
              </React.Fragment>
            ))}

            {/* Utility targets as simple clickable rows */}
            {utilityTargets.map((t, idx) => (
              <UtilityRow type="button" role="menuitem" key={`util-${idx}`} onClick={t.onClick}>
                {t.icon && <span>{t.icon}</span>}
                {t.label}
              </UtilityRow>
            ))}
          </React.Fragment>
        );
      })}

      {/* Keep backward compatibility with onCopy prop */}
      {onCopy && (
        <UtilityAction type="button" role="menuitem" onClick={onCopy}>
          <span>📋</span> {copyLabel}
        </UtilityAction>
      )}
    </MenuStyle>
  );
};
